import { useDispatch, useSelector } from 'react-redux';
import { saveShippingMethod } from '../../slices/cartSlice';
import {
  SHIPPING_RATES,
  FREE_SHIPPING_THRESHOLD,
  formatNTD,
} from '../../utils/pricing';

// Selectable delivery-carrier picker for the one-page store. Mirrors the
// existing checkout pattern exactly: selecting a carrier dispatches
// saveShippingMethod -> cartSlice -> updateCart() -> localStorage, so the
// choice is the single source of truth and survives a refresh just like the
// address and payment method. Icons are generic (envelope / parcel) with the
// carrier name as the label.
export const SHIPPING_METHODS = [
  { id: 'post', name: '中華郵政', subtitle: '郵局宅配 · 3–5 個工作天' },
  { id: 'tcat', name: '黑貓宅配', subtitle: '宅急便 · 隔日到貨（低溫可選）' },
];

const CarrierIcon = ({ id, color }) => {
  if (id === 'post') {
    return (
      <svg width="26" height="26" viewBox="0 0 24 20" fill="none" stroke={color}
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="0.75" y="1" width="22.5" height="18" rx="3" />
        <path d="M1.5 3l10.5 8 10.5-8" />
      </svg>
    );
  }
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 1l11 6v10l-11 6-11-6V7z" />
      <path d="M1 7l11 6 11-6M12 13v10" />
    </svg>
  );
};

const ShippingMethods = () => {
  const dispatch = useDispatch();
  const { shippingMethod, itemsPrice } = useSelector((state) => state.cart);
  const selectedId = shippingMethod?.id;
  const freeShipping = Number(itemsPrice) >= FREE_SHIPPING_THRESHOLD;
  const toFree = FREE_SHIPPING_THRESHOLD - Number(itemsPrice || 0);

  const select = (m) => () => {
    // Re-clicking the selected carrier keeps its details (e.g. 黑貓 time slot)
    if (selectedId === m.id) return;
    dispatch(saveShippingMethod({ id: m.id, name: m.name }));
  };

  return (
    <div className="mb-4">
      {/* Sub-block heading (h6) under the "Shipping" section title */}
      <h6 className="op-subtitle">配送方式</h6>
      {/* Free-shipping nudge: how far the shopper is from NT$1,000 */}
      <p className="op-free-hint" data-testid="free-ship-hint">
        {freeShipping
          ? `已享滿 ${formatNTD(FREE_SHIPPING_THRESHOLD)} 免運`
          : `再買 ${formatNTD(toFree)} 即可享免運（滿 ${formatNTD(FREE_SHIPPING_THRESHOLD)}）`}
      </p>
      <div className="d-flex flex-column" style={{ gap: '10px' }}>
        {SHIPPING_METHODS.map((m) => {
          const active = selectedId === m.id;
          const dark = m.id === 'tcat';
          return (
            <button
              type="button"
              key={m.id}
              onClick={select(m)}
              aria-pressed={active}
              className="text-start d-flex align-items-center w-100"
              style={{
                gap: '14px',
                padding: '10px 14px',
                borderRadius: '16px',
                background: '#FFFCF6',
                border: `1.5px solid ${active ? 'var(--brand-primary, #A2611A)' : '#EBDAC4'}`,
                boxShadow: active ? '0 0 0 3px var(--brand-primary-ring, rgba(216,151,74,0.25))' : 'none',
                cursor: 'pointer',
                transition: 'border-color .12s, box-shadow .12s',
              }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: '46px', height: '46px', borderRadius: '12px',
                  background: dark ? '#3A342E' : 'var(--brand-primary-soft, #F3E7D5)',
                  // icon strokes use currentColor so they can follow the theme token
                  color: dark ? '#FBF4EA' : 'var(--brand-primary, #A2611A)',
                }}
              >
                <CarrierIcon id={m.id} color="currentColor" />
              </span>

              <span className="flex-grow-1">
                <span className="d-block" style={{ fontWeight: 700, color: '#4A3526' }}>{m.name}</span>
                <span className="d-block" style={{ fontSize: '12px', fontWeight: 600, color: '#6E5847' }}>
                  {m.subtitle}
                </span>
              </span>

              {/* Carrier price: flat rate, or struck through when free */}
              <span className="op-rate flex-shrink-0" data-testid={`rate-${m.id}`}>
                {freeShipping ? (
                  <>
                    <s>{formatNTD(SHIPPING_RATES[m.id])}</s> 免運
                  </>
                ) : (
                  formatNTD(SHIPPING_RATES[m.id])
                )}
              </span>

              <span
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  border: `2px solid ${active ? 'var(--brand-primary, #A2611A)' : '#D8B98F'}`,
                }}
              >
                {active && (
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--brand-primary, #A2611A)' }} />
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ShippingMethods;
